/** Shared public header and footer for landing and account pages, on kit components. */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html, repeat } from 'hydrogen/runtime';

import { LinkButton } from './kit.js';
import { MatrixLockup } from './public-brand.js';
import { LANDING_PATH, SIGN_IN_PATH, SIGN_UP_PATH } from './public-paths.js';

type PublicLink = { href: string; label: string; nav?: boolean };
type FooterColumn = { heading: string; links: readonly PublicLink[] };

const PRIMARY_LINKS: readonly PublicLink[] = [
  { href: '/#replaces', label: 'What replaces what' },
  { href: '/#how', label: 'How it works' },
  { href: '/#compare', label: 'Proof' },
  { href: '/#benefits', label: 'Why Matrix' },
  { href: '/#tools', label: 'Works with Git' },
  { href: '/#beta', label: 'Beta' },
];

/**
 * Every footer destination resolves to something that exists. A link to a page
 * Matrix does not serve — careers, a blog, a status page — is a decoy, and a
 * decoy in the footer is the same defect class as an invented KPI on the hero.
 */
const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    heading: 'Product',
    links: [
      { href: '/#replaces', label: 'What replaces what' },
      { href: '/#how', label: 'How it works' },
      { href: '/#benefits', label: 'Why Matrix' },
      { href: '/#tools', label: 'Works with Git' },
    ],
  },
  {
    heading: 'Proof',
    links: [
      { href: '/#compare', label: 'GitHub versus matrixbased.com' },
      { href: '/#law', label: 'The scaling law' },
      { href: '/#receipts', label: 'Receipts' },
    ],
  },
  {
    heading: 'Beta',
    links: [
      { href: '/#beta', label: 'Request an invite' },
      { href: SIGN_UP_PATH, label: 'Redeem an invite', nav: true },
      { href: SIGN_IN_PATH, label: 'Sign in', nav: true },
    ],
  },
];

function PublicLinkRow(link: PublicLink): TemplateInstance {
  if (link.nav) {
    return html`<a class="mx-public-link" href="${link.href}" data-nav="${link.href}">${link.label}</a>`;
  }
  return html`<a class="mx-public-link" href="${link.href}">${link.label}</a>`;
}

/** A section's eyebrow, heading and lede: the shadcn section header. */
export function SectionHead(options: { kicker: string; title: TemplateInstance | string; lede?: TemplateInstance | string }): TemplateInstance {
  return html`
    <header class="mx-public-section__head">
      <p class="mx-public-kicker">${options.kicker}</p>
      <h2>${options.title}</h2>
      ${options.lede === undefined ? html`` : html`<p class="mx-public-lede">${options.lede}</p>`}
    </header>
  `;
}

function Wordmark(size: 'chrome' | 'lead' = 'chrome'): TemplateInstance {
  return html`
    <a class="mx-public-wordmark" href="${LANDING_PATH}" data-nav="${LANDING_PATH}" aria-label="Matrix">
      ${MatrixLockup({ className: `mx-public-lockup mx-public-lockup--${size}` })}
    </a>
  `;
}

function FooterColumnBlock(column: FooterColumn): TemplateInstance {
  return html`
    <div class="mx-public-foot__column">
      <h2>${column.heading}</h2>
      <nav aria-label="${column.heading}">
        ${repeat(column.links, (link) => link.label, PublicLinkRow)}
      </nav>
    </div>
  `;
}

/**
 * An account page carries no marketing nav. Someone part-way through signing in
 * is not choosing between Product and Pricing.
 */
export function PublicWordmark(): TemplateInstance {
  return html`
    <header class="mx-public-top mx-public-top--bare">
      <div class="mx-public-bar">${Wordmark('lead')}</div>
    </header>
  `;
}

export function PublicHeader(options: { path: string }): TemplateInstance {
  return html`
    <header class="mx-public-top" data-path="${options.path}">
      <div class="mx-public-bar">
        ${Wordmark()}
        <nav class="mx-public-links" aria-label="Marketing">
          ${repeat(PRIMARY_LINKS, (link) => link.label, PublicLinkRow)}
        </nav>
        <div class="mx-public-top__actions">
          ${LinkButton({ href: SIGN_IN_PATH, label: 'Sign in', variant: 'ghost' })}
          ${LinkButton({ href: '/#beta', label: 'Request invite', spa: false })}
        </div>
      </div>
    </header>
  `;
}

export function PublicFooter(): TemplateInstance {
  return html`
    <footer class="mx-public-foot">
      <div class="mx-public-foot__grid">
        <div class="mx-public-foot__brand">
          ${Wordmark()}
          <p>The forge for software written by agents. Invite-only beta.</p>
        </div>
        ${repeat(FOOTER_COLUMNS, (column) => column.heading, FooterColumnBlock)}
      </div>
      <div class="ui-separator" data-slot="separator" data-orientation="horizontal" role="none"></div>
      <p class="mx-public-foot__note">
        Account and billing services are not Matrix authority. Canonical software state lives in the
        Authority Fabric; Git and GitHub mirrors are projection.
      </p>
    </footer>
  `;
}
